<template>
  <div class="container pull-up">
    <div class="row">
      <div class="col-md-12">
        <v-card class="card m-b-30">
          <v-card-title class="d-flex align-center justify-space-between pb-0 compact-header">
            <div class="d-flex flex-row align-center flex-grow-1 w-100">
              <div class="d-flex align-center pr-2">
                <v-icon class="mr-1" color="primary">mdi-office-building</v-icon>
                <v-select
                  v-model="department"
                  :items="departments"
                  item-text="name"
                  item-value="id"
                  label="Store"
                  dense
                  outlined
                  hide-details
                  class="compact-select"
                  :disabled="!departments.length"
                  :loading="!departments.length"
                  style="min-width: 180px;"
                >
                  <template v-slot:no-data>
                    <span class="error">No department found</span>
                  </template>
                </v-select>
              </div>
              <div class="d-flex align-center pr-2 ml-2">
                <v-icon class="mr-1" color="primary">mdi-format-list-numbered</v-icon>
                <v-select
                  v-model="perPage"
                  :items="itemsPerPageOptions"
                  label="Items per page"
                  dense
                  outlined
                  hide-details
                  class="compact-select"
                  style="min-width: 150px;"
                  @change="perPageChanged"
                >
                </v-select>
                <v-progress-circular
                  v-if="isDownloading"
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </div>
              <div class="d-flex align-center ml-auto">
                <pagination :total="total" :page-size="perPage" @current-change="pageChanged"/>
                <div v-if="selected.length" class="d-flex align-center ml-2">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon color="primary" v-bind="attrs" v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="checkAll">
                        <v-list-item-icon><v-icon color="info">mdi-checkbox-multiple-marked</v-icon></v-list-item-icon>
                        <v-list-item-title>Select All</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="unCheckAll">
                        <v-list-item-icon><v-icon color="warning">mdi-checkbox-multiple-blank-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>Unselect All</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="printReportMultiple" :disabled="isDownloading">
                        <v-list-item-icon><v-icon color="success">mdi-printer</v-icon></v-list-item-icon>
                        <v-list-item-title>Print Report</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="printServiceMultiple" :disabled="isDownloading">
                        <v-list-item-icon><v-icon color="primary">mdi-printer</v-icon></v-list-item-icon>
                        <v-list-item-title>Print Service</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="generateSessionReport(null)" :disabled="isDownloading">
                        <v-list-item-icon><v-icon color="purple">mdi-file-pdf-box</v-icon></v-list-item-icon>
                        <v-list-item-title>Download Report PDF</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="generateServiceReport(null)" :disabled="isDownloading">
                        <v-list-item-icon><v-icon color="primary">mdi-file-pdf-box</v-icon></v-list-item-icon>
                        <v-list-item-title>Service PDF</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-divider/>
            <v-row :gutter="5" align="center" justify="center">
              <el-card v-if="!sessions.length"><i>No available session for this department</i></el-card>
              <v-col xl="3" cols="auto" v-for="(session, idx) in sessions" :key="idx">
                <v-card class="d-flex flex-column" height="100%">
                  <v-card-title class="session-title">
                    <i class="fa fa-dot-circle-o " :class="[session.status ? 'session-open' : 'session-close']"></i>
                    &nbsp; {{ session.user.name | limit(18) }}
                    <v-spacer/>
                    <v-checkbox class="ma-0" color="success" hide-details :checked="!!selected.find(s => s.id === session.id)"
                                 v-model="sessions[idx].selected"
                                 :disabled="session.status"></v-checkbox>
                  </v-card-title>
                  <v-card-text>
                    <span>Opened By: {{ session.opener.name }}</span> <br>
                    <span v-if="session.closer">Closed By: {{ session.closer.name }}</span> <br>
                    <span v-if="session.closer">Closing Time: {{ session.closed_on | datetime }}</span>
                  </v-card-text>
                  <v-divider class="my-0 mx-4"/>
                  <v-card-actions class="d-flex justify-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn x-small color="error" depressed v-can.disable="'session.close'" :disabled="!session.status"
                               @click="closeSession(session)" v-bind="attrs" v-on="on">Close
                          <el-icon name="close"/>
                        </v-btn>
                      </template>
                      <span>Close this session</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn x-small color="success" v-can.disable="'session.print'" :disabled="session.status || isDownloading"
                               @click.prevent="printInventoryReport([session.id])" class="mx-1" v-bind="attrs" v-on="on">Report
                          <el-icon name="printer"/>
                        </v-btn>
                      </template>
                      <span>Print session report</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn x-small color="info" v-can.disable="'session.print'" :disabled="session.status || isDownloading"
                               @click.prevent="generateSessionReport([session.id])" class="mr-1" v-bind="attrs" v-on="on">PDF
                          <el-icon name="document"/>
                        </v-btn>
                      </template>
                      <span>Generate PDF report</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn x-small color="primary" v-can.disable="'session.print-service'" :disabled="session.status || isDownloading"
                               @click.prevent="printServiceReport([session.id])" class="" v-bind="attrs" v-on="on">Service
                          <el-icon name="dish"/>
                        </v-btn>
                      </template>
                      <span>Print service report</span>
                    </v-tooltip>
                  </v-card-actions>
                  <v-divider class="my-0 mx-4"/>
                  <v-card-text class="py-2">
                    <v-row dense justify="space-between">
                      <v-col><small class="text-muted">{{ (session.created_at) | date }}</small></v-col>
                      <v-col cols="auto">
                        <small :class="session.status ? 'success--text' : 'error--text'">
                          {{ session.status ? 'Open' : 'Closed' }}
                        </small>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="py-4 d-flex justify-end">
            <pagination :total="total" :page-size="perPage" @current-change="pageChanged" :current-page="currentPage"/>
          </v-card-actions>
        </v-card>
      </div>
    </div>
    <!-- Closing Amount Dialog -->
    <v-dialog v-model="closeDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">Close Session</v-card-title>
        <v-card-text class="pt-4">
          <p>Please, specify the closing amount and click continue to close the session.</p>
          <v-text-field
            v-model.number="closingAmount"
            label="Closing Amount"
            type="number"
            outlined
            dense
            autofocus
            :rules="amountRules"
          >
            <template #prepend-inner>
              <v-icon>mdi-cash</v-icon>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="cancelCloseSession">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="confirmCloseSession" :disabled="!closingAmount || isNaN(closingAmount) || closingAmount <= 0">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Pagination from '../_Partials/Pagination.vue'

export default {
  name: 'session-index',
  components: { Pagination },
  data () {
    return {
      departments: [],
      sessions: [],
      department: null,
      loading: false,
      showing: {},
      checked: [],
      perPage: 9,
      itemsPerPageOptions: [9, 18, 27, 36, 45, 90],
      total: 0,
      currentPage: 1,
      closeDialog: false,
      sessionToClose: null,
      closingAmount: '',
      isDownloading: false,
      amountRules: [
        v => !!v || 'Amount is required',
        v => !isNaN(v) || 'Please enter a valid number',
        v => v > 0 || 'Amount must be greater than 0'
      ]
    }
  },
  watch: {
    department () {
      this.resetPagination()
      this.fetchSessions()
    },
    currentPage (page) {
      this.fetchSessions()
    },
    perPage () {
      this.resetPagination()
      this.fetchSessions()
    }
  },
  computed: {
    selected () {
      return this.sessions.filter(s => s.selected && !s.status)
    }
  },
  methods: {
    isChecked (id) {
      return this.sessions.find(s => s.id === id).selected
    },
    checkAll () {
      this.sessions.filter(s => !s.status && !s.selected).forEach(s => (s.selected = true))
    },
    unCheckAll () {
      this.sessions.filter(s => !s.status && s.selected).forEach(s => (s.selected = false))
    },
    printReportMultiple () {
      this.printInventoryReport(this.selected.map(s => s.id))
    },
    printServiceMultiple () {
      this.printServiceReport(this.selected.map(s => s.id))
    },
    fetchSessions () {
      this.$sessionService.getSessions(`${this.department}`, this.perPage, this.currentPage)
        .then(data => {
          this.sessions = this.addRequiredFields(data.data)
          this.total = data.total
        })
        .catch(err => {
          this.displayMessage(err.message, () => {
          }, 'error')
        })
    },
    pageChanged (page) {
      this.currentPage = page
    },
    perPageChanged () {
      // The watch on perPage will handle resetting pagination and fetching sessions
    },
    resetPagination () {
      this.currentPage = 1
      this.total = 0
    },
    addRequiredFields (data) {
      return data.map(s => {
        s.selected = false
        return s
      })
    },
    getDepartments () {
      this.$departmentService.getDepartments()
        .then((data) => {
          this.departments = data
          if (this.departments.length && !this.department) {
            this.department = this.departments[0].id
          }
        })
    },
    closeSession (session) {
      if (session) {
        this.sessionToClose = session
        this.closingAmount = ''
        this.closeDialog = true
      }
    },

    cancelCloseSession () {
      this.closeDialog = false
      this.sessionToClose = null
      this.closingAmount = ''
    },

    confirmCloseSession () {
      if (!this.sessionToClose || !this.closingAmount) return

      // Close the dialog
      this.closeDialog = false

      // Prepare data with closing amount
      const closeData = {
        closing_amount: this.closingAmount
      }

      // Call the service with the closing amount
      this.$sessionService.closeUserSession(this.sessionToClose.id, closeData)
        .then(data => {
          const index = this.sessions.indexOf(this.sessions.find(s => this.sessionToClose.id === s.id))
          this.sessions.splice(index, 1, data)
          this.displayMessage(this.$t('session.message.closed'), () => {
            this.askQuestion(this.$t('session.confirm.reopen_new', [data.user.name]), () => {
              this.$sessionService.createSession({
                user: data.user_id,
                department: data.department_id
              })
                .then(data2 => {
                  this.displayMessage(this.$t('session.message.created'), () => {
                    this.sessions.splice(0, 0, this.addRequiredFields([data2])[0])
                  })
                })
            }, this.$t('title.question'), () => {
              this.notify(this.$t('session.message.remember_to_open'), '', 'warning')
            })
          })
        })
        .catch(error => {
          this.displayMessage(error.message || 'Error closing session', () => {}, 'error')
        })
        .finally(() => {
          // Reset state
          this.sessionToClose = null
          this.closingAmount = ''
        })
    },
    printInventoryReport (data) {
      this.$printService.printSessions(data)
        .then(res => {
        })
        .catch(err => {
          err = err.response
          if (err && err.data && err.data.status === 'error') {
            this.displayMessage(err.data.message, () => {
            }, 'warning')
          }
        })
    },
    printServiceReport (data) {
      this.$printService.printServices(data)
        .then(res => {
        })
        .catch(err => {
          err = err.response
          if (err && err.data && err.data.status === 'error') {
            this.displayMessage(err.data.message, () => {
            }, 'warning')
          }
        })
    },
    async generateSessionReport (data) {
      if (!data) {
        data = this.selected.map(s => s.id)
      }
      this.isDownloading = true
      try {
        const response = await this.$sessionService.generateReport({ sessions: data, download: true })
        // Create download link
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Extract filename from response headers
        const contentDisposition = response.headers['content-disposition']
        let filename = `session_report_${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/)
          if (filenameMatch) {
            filename = filenameMatch[1]
          }
        }

        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        if (err && err.data && err.data.status === 'error') {
          this.displayMessage(err.data.message, () => {
          }, 'warning')
        }
      } finally {
        this.isDownloading = false
      }
    },
    async generateServiceReport (data) {
      if (!data) {
        data = this.selected.map(s => s.id)
      }
      this.isDownloading = true
      try {
        const response = await this.$sessionService.generateServiceReport({ sessions: data, download: true })
        // Create download link
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Extract filename from response headers
        const contentDisposition = response.headers['content-disposition']
        let filename = `service_report_${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/)
          if (filenameMatch) {
            filename = filenameMatch[1]
          }
        }

        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.log(err)
        if (err && err.data && err.data.status === 'error') {
          this.displayMessage(err.data.message, () => {
          }, 'warning')
        }
      } finally {
        this.isDownloading = false
      }
    }
  },
  mounted () {
    this.$emit('parent-title', this.$t('session.name'))
    if (this.$route.query.department) {
      setTimeout(() => {
        this.department = this.$route.query.department
      }, 800)
    }
    this.getDepartments()
  }
}
</script>
<style scoped>
.session-open {
  color: #00a513;
}

.session-close {
  color: #ef1900;
}

.full-width {
  width: 100% !important;
}

.session-title {
  font-weight: bold;
  /*font-family: Niconne,monospace;*/
  font-size: 20px;
}

.compact-header {
  min-height: 56px;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.compact-select {
  max-width: 220px;
}
</style>
